'use client';

import { useEffect } from 'react';
import { isChunkLoadError, reloadOnceForChunkError, reportError } from '@/lib/error-reporter';

/**
 * Last-resort boundary: catches render errors in the ROOT layout itself (where `error.tsx` cannot
 * help, because the layout that would host it is the thing that crashed). It must render its own
 * <html>/<body>.
 *
 * Without this and `error.tsx`, Next's built-in boundary swallowed every React render/lifecycle
 * error — the user saw a broken screen and nothing ever reached the admin Errors page.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    // Stale tab after a deploy (old chunk gone): reload once onto the new build instead of
    // reporting an error that reset() cannot fix.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    reportError(error, {
      type: 'react-global-error',
      // `digest` is Next's server-side error id — it's the only way to correlate this with the
      // server log line when the message was redacted in production.
      digest: error.digest,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, background: '#0a0a0a', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Something went wrong</h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0, maxWidth: 420 }}>
            The page failed to load. We&apos;ve been notified — try again, and if it keeps happening
            let us know.
          </p>
          <button
            onClick={chunkError ? () => window.location.reload() : reset}
            style={{
              marginTop: 8,
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: '#7C3AED',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
